import { Identified } from "@/types/sdk-types";
import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { PatientControllerApi, Patient } from "../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../utils/utils";

interface UsePatientsListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  statusKey?: boolean;
  providerGroupId?: string | null;
  locationUuid?: string;
  providerUuid?: string;
  referringAgencyUuid?: string[];
  ageFilterCategory?: string;
  ageFilterValue?: number;
  isReferral?: boolean;
  enabled?: boolean;
}

/**
 *
 * @author Shrinivas Mandhare
 */

export function usePatientsList({
  gridSorting,
  pagination,
  searchKey,
  providerGroupId,
  locationUuid,
  statusKey,
  providerUuid,
  referringAgencyUuid,
  ageFilterCategory,
  ageFilterValue,
  isReferral,
  enabled = true,
}: UsePatientsListProps): useBaseContent<Identified<Patient>> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  const sortData = gridSorting?.[0];

  return useQuery({
    queryKey: [
      "provider-group-patients-list",
      pageSize,
      pageIndex,
      providerGroupId,
      locationUuid,
      providerUuid,
      referringAgencyUuid,
      searchKey,
      statusKey,
      gridSorting,
      ageFilterCategory,
      ageFilterValue,
      isReferral,
    ],
    enabled: !!providerGroupId && enabled,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      new PatientControllerApi(getConfigObject())
        .getAllPatient(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          statusKey, //status
          undefined, //archive
          locationUuid, //locationUuid
          providerUuid, //providerUuid
          referringAgencyUuid, //referringAgencyUuid
          undefined, //ssn
          undefined, //phone
          ageFilterCategory,
          ageFilterValue,
          undefined, //dateOfBirth
          isReferral, //isReferral
          searchKey, //searchString
          providerGroupId! //xTENANTID
        )
        .then((it) => it?.data),
  });
}
